import React, { useEffect, useState, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { Inline, Box } from '@atlaskit/primitives/compiled';
import { ModalTitle, CloseButton } from '@atlaskit/modal-dialog';

import { ReverseTrialLozengeExperiment } from '@confluence/trial-components/entry-points/ReverseTrialLozenge.tsx';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { AccessRequestsButton } from '@confluence/access-requests/entry-points/AccessRequestButton.tsx';
import { fg } from '@confluence/feature-gating';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller.tsx';

import { OverflowMenu, type OverflowMenuProps } from './OverflowMenu.tsx';

export type TitleAndButtonsProps = {
	title: keyof typeof titles;
	overflowMenu: OverflowMenuProps | undefined;
	onBackClick?: () => void; // back button only displayed if this is present
	onCloseClick: () => void;
	accessRequests?: {
		lastTriggeredDialogVariant: 'publicLink' | 'shareToSlack' | 'accessRequests' | null;
		usdLoading?: boolean;
		openAccessRequestsDialog: () => void;
		clearElementToFocus: () => void;
	};
};

const titles = defineMessages({
	share: {
		id: 'share-and-restrict-dialog.header.title.share',
		defaultMessage: 'Share',
		description: 'Modal dialog title for the share and restrict dialog',
	},
	publicLink: {
		id: 'share-and-restrict-dialog.header.title.public-link',
		defaultMessage: 'Public link',
		description: 'Text is for the title of the Public link dialog',
	},
	slack: {
		id: 'share-and-restrict-dialog.header.title.slack',
		defaultMessage: 'Share to Slack',
		description: 'Text is for the title of the Share to Slack dialog',
	},
	accessRequests: {
		id: 'share-and-restrict-dialog.header.title.accessRequests',
		defaultMessage: 'Requests',
		description: 'Text is for the title of the Access Requests dialog',
	},
	specificAccess: {
		id: 'share-and-restrict-dialog.header.title.specificAccess',
		defaultMessage: 'Specific access',
		description: 'Text is for the title of the Specific access dialog',
	},
});

const i18n = defineMessages({
	close: {
		id: 'share-and-restrict-dialog.header.close',
		defaultMessage: 'Close',
		description: 'Screen-reader label for button that closes a modal dialog',
	},
	back: {
		id: 'share-and-restrict-dialog.header.back',
		defaultMessage: 'Back',
		description: 'Screen-reader label for button that goes back to the previous screen',
	},
});

export const TitleAndButtons = ({
	title,
	overflowMenu,
	onCloseClick,
	onBackClick,
	accessRequests,
}: TitleAndButtonsProps) => {
	const focusRef = useRef<any>(null);

	const [shouldFocusAccessRequestsButton, setShouldFocusAccessRequestsButton] = useState(false);

	const skipCloseButtonFocusWhenAccessRequestsButtonIsPresent =
		!!accessRequests && fg('confluence_frontend_stateful_access_requests');

	const onAccessRequestsButtonDataLoaded = (isVisible: boolean) => {
		if (accessRequests?.lastTriggeredDialogVariant === 'accessRequests') {
			accessRequests.clearElementToFocus();
			if (isVisible) {
				setShouldFocusAccessRequestsButton(true);
			} else {
				focusRef.current?.focus();
			}
		}
	};

	/** Determine if we should focus Close button */
	useEffect(() => {
		// When the dialog title changes, it means the content inside has changed.
		// For example, in the public links or Slack dialogs, this could cause focus to be lost.
		// This check helps make sure focus stays where it should.
		if (!skipCloseButtonFocusWhenAccessRequestsButtonIsPresent && focusRef.current && title) {
			focusRef.current.focus();
		}
	}, [title, skipCloseButtonFocusWhenAccessRequestsButtonIsPresent]);

	const { formatMessage } = useIntl();
	return (
		<Inline grow="fill" spread="space-between">
			<Inline grow="fill" alignBlock="center" space="space.150">
				{onBackClick && (
					<IconButton
						icon={ArrowLeftIcon}
						label={formatMessage(i18n.back)}
						appearance="subtle"
						onClick={onBackClick}
					/>
				)}
				<ModalTitle>
					<FormattedMessage {...titles[title]} />
					{title === 'publicLink' && (
						<ReverseTrialLozengeExperiment
							touchpointId="confluence_public_link_dialog_header"
							edition={ConfluenceEdition.STANDARD}
						/>
					)}
				</ModalTitle>
			</Inline>

			<Inline space="space.050">
				{accessRequests && fg('confluence_frontend_stateful_access_requests') && (
					<AccessRequestsButton
						/**
						 * We want to make sure that while USD is loading for the first time
						 * That the button is hidden for a more seamless UX
						 */
						delayShowing={!!accessRequests.usdLoading}
						/** We want to auto focus if coming back from Access Requests Dialog and the button is visible */
						shouldAutoFocus={shouldFocusAccessRequestsButton}
						/** Once we know the button is focused we need to clean-up the USD state that tells us to focus it in the first place */
						onAutoFocus={() => setShouldFocusAccessRequestsButton(false)}
						onClick={accessRequests.openAccessRequestsDialog}
						onDataLoaded={onAccessRequestsButtonDataLoaded}
						tryCacheFirst={!!accessRequests.lastTriggeredDialogVariant}
						source={SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE}
					/>
				)}
				{overflowMenu && <OverflowMenu {...overflowMenu} />}
				<Box ref={focusRef}>
					<CloseButton label={formatMessage(i18n.close)} onClick={onCloseClick} />
				</Box>
			</Inline>
		</Inline>
	);
};
